<template>
  <div :class="classes">
    <VLoadingDots v-if="loading" mask />

    <div v-if="$slots.ribbon" class="v-card__ribbon" :class="{ [`v-card__ribbon--${ribbonType}`]: ribbonType }">
      <slot name="ribbon" />
    </div>

    <div v-if="headerVisible" class="v-card__header">
      <slot name="title">
        <div class="title-20" v-text="title" />
      </slot>

      <div class="v-card__header-left">
        <slot name="header-left" />
      </div>

      <div class="v-card__header-right">
        <slot name="header-right" />
      </div>
    </div>

    <div class="v-card__body">
      <div class="v-card__body-content">
        <slot />
      </div>

      <div v-if="$slots.expand" class="v-card__expand">
        <VTransitionCollapse lazy :visible="expanded">
          <div class="v-card__expand-content">
            <slot name="expand" />
          </div>
        </VTransitionCollapse>

        <div class="v-card__expand-icon-container" @click.stop="expanded = !expanded">
          <VIcon class="v-card__expand-icon" :class="{ 'is-expanded': expanded }" icon="ChevronDown" type="primary" />
        </div>
      </div>
    </div>

    <div v-if="$slots.footer" class="v-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import type { VCardRibbonType, VCardSize, VCardType } from '../types.ts'
import VIcon from './Icon/VIcon.vue'
import VLoadingDots from './VLoadingDots.vue'
import VTransitionCollapse from './VTransitionCollapse.vue'

interface Props {
  title?: string
  loading?: boolean
  noBorder?: boolean
  clickable?: boolean
  ribbonType?: VCardRibbonType
  type?: VCardType
  size?: VCardSize
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  type: '',
  ribbonType: undefined,
  loading: false,
  clickable: false,
  noBorder: false,
  size: '',
})

const slots = defineSlots<{
  default?(): any
  title?(): any
  ribbon?(): any
  footer?(): any
  expand?(): any
  'header-left'?(): any
  'header-right'?(): any
}>()

const expanded = ref(false)

const classes = useClassName(
  'v-card',
  computed(() => [props.title]),
  computed(() => ({
    'v-card--bordered': !props.noBorder,
    'is-clickable': props.clickable,
    [`v-card--${props.type}`]: props.type,
    [`v-card--${props.size}`]: props.size,
  })),
)

const headerVisible = computed(() => props.title || slots.title || slots['header-left'] || slots['header-right'])
</script>

<style>
.v-card {
  background-color: #fff;
  border-radius: 10px;
  margin-bottom: 30px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.is-clickable {
    cursor: pointer;
    transition: box-shadow var(--transition);

    &:hover {
      box-shadow: var(--box-shadow-light);
    }
  }
}

.v-card--bordered {
  border: var(--border);
}

.v-grid--stretch > .v-col > .v-card {
  height: calc(100% - 30px);
}

.v-card__ribbon {
  position: absolute;
  right: 20px;
  top: 0;
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  border-top: 0;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  padding: 2px 10px;
}

.v-card__ribbon--primary {
  background-color: var(--color-primary);
  color: #fff;
}

.v-card__header {
  position: relative;
  padding: 20px 30px 20px 30px;
  padding-bottom: 0;
  display: flex;
  align-items: center;

  .v-card__header-left {
    display: flex;
    align-items: center;
    padding-left: 15px;
    flex-grow: 1;
  }

  .v-card__header-right {
    display: flex;
    align-items: center;
  }

  & + .v-card__body {
    padding-top: 20px;
  }
}

.v-card__body {
  position: relative;
  padding: 30px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.v-card__footer {
  position: relative;
  padding: 30px;
  text-align: right;
}

.v-card--primary {
  background-color: var(--color-blue-1);
  border-color: var(--color-blue-5);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-blue-3);
  }
}

.v-card--info {
  background-color: var(--color-grey-6);
  border-color: var(--color-grey-20);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-grey-10);
  }
}

.v-card--page-background {
  background-color: var(--color-page-background);
  border-color: var(--color-grey-8);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-grey-6);
  }
}

.v-card--green {
  background-color: var(--color-green-lightest);
  border-color: var(--color-green);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-green-4);
  }
}

.v-card--yellow {
  background-color: var(--color-yellow-lightest);
  border-color: var(--color-yellow);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-yellow-4);
  }
}

.v-card--red {
  background-color: var(--color-red-lightest);
  border-color: var(--color-red);

  .v-card__expand .v-card__expand-icon-container:hover {
    background-color: var(--color-red-4);
  }
}

.v-card__expand-icon {
  width: 100% !important;
  margin-bottom: -3px;
}

.v-card__expand-icon-container {
  cursor: pointer;
  margin: 15px -30px -30px -30px;
  padding: 0 30px;
  transition: background-color var(--transition);

  &:hover {
    background-color: var(--color-grey-6);
  }
}

@media (max-width: 768px) {
  .v-card__header {
    padding: 15px;
    padding-bottom: 0;
  }

  .v-card__body {
    padding: 15px;
  }

  .v-card__expand {
    margin: 15px -15px -15px -15px;
    padding: 0 15px;
  }

  .v-card__expand-content {
    padding-top: 15px;
  }
}

.v-card--small {
  .v-card__header,
  .v-card__body,
  .v-card__footer {
    padding: 20px;
  }

  .v-card__expand-icon-container {
    margin: 15px -30px -20px -30px;
  }
}
</style>
